import React, { useRef, forwardRef, useImperativeHandle, useState } from "react";
import { API, FONT_OPTIONS } from "@/lib/api";
import { WS } from "@/constants/testIds";
import { Move } from "lucide-react";

/**
 * Mockup canvas — renders the storefront photo and an overlaid sign text element
 * that can be dragged. Text font-size is sized via CSS container-relative units
 * to avoid needing React state for container dimensions.
 *
 * Imperative API (via ref):
 *   - exportPng(): Promise<Blob>  — composites mockup to PNG at natural image
 *                                   resolution.
 *   - fitWidth() / centerText()
 */
const MockupCanvas = forwardRef(function MockupCanvas(
  { storefrontPath, signText, mockup, onChange, night },
  ref,
) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const dragState = useRef(null);

  const font = FONT_OPTIONS.find(f => f.id === mockup.font_family) || FONT_OPTIONS[0];

  const startDrag = (e) => {
    if (!containerRef.current) return;
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const startPos = { x: mockup.pos_x_pct, y: mockup.pos_y_pct };
    dragState.current = { startX, startY, startPos };
    const onMove = (ev) => {
      const dx = ev.clientX - dragState.current.startX;
      const dy = ev.clientY - dragState.current.startY;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      onChange({
        ...mockup,
        pos_x_pct: clamp(dragState.current.startPos.x + (dx / w) * 100, 0, 100),
        pos_y_pct: clamp(dragState.current.startPos.y + (dy / h) * 100, 0, 100),
      });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const startTouchDrag = (e) => {
    if (!containerRef.current) return;
    const t = e.touches[0];
    const startX = t.clientX, startY = t.clientY;
    const startPos = { x: mockup.pos_x_pct, y: mockup.pos_y_pct };
    dragState.current = { startX, startY, startPos };
    const onMove = (ev) => {
      const t2 = ev.touches[0];
      const dx = t2.clientX - dragState.current.startX;
      const dy = t2.clientY - dragState.current.startY;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      onChange({
        ...mockup,
        pos_x_pct: clamp(dragState.current.startPos.x + (dx / w) * 100, 0, 100),
        pos_y_pct: clamp(dragState.current.startPos.y + (dy / h) * 100, 0, 100),
      });
    };
    const onUp = () => {
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };

  useImperativeHandle(ref, () => ({
    async exportPng() {
      const img = imgRef.current;
      if (!img || !img.naturalWidth) throw new Error("Image not ready");
      const W = img.naturalWidth;
      const H = img.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(img, 0, 0, W, H);

      if (night) {
        ctx.fillStyle = "rgba(0,0,0,0.55)";
        ctx.fillRect(0, 0, W, H);
      }

      const text = (signText || "").trim();
      if (text) {
        ctx.save();
        const fontSizePx = (mockup.height_pct / 100) * H;
        ctx.font = `${Math.round(fontSizePx)}px ${font.css}`;
        ctx.fillStyle = mockup.color;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        if ("letterSpacing" in ctx) {
          ctx.letterSpacing = `${(mockup.letter_spacing || 0)}px`;
        }
        const cx = (mockup.pos_x_pct / 100) * W;
        const cy = (mockup.pos_y_pct / 100) * H;
        ctx.translate(cx, cy);
        ctx.rotate(((mockup.rotation || 0) * Math.PI) / 180);

        if (mockup.shadow) {
          ctx.shadowColor = "rgba(0,0,0,0.55)";
          ctx.shadowBlur = Math.round(fontSizePx * 0.08);
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = Math.round(fontSizePx * 0.04);
        }

        if (night && mockup.glow) {
          for (let i = 0; i < 4; i++) {
            ctx.shadowColor = mockup.color;
            ctx.shadowBlur = Math.round(fontSizePx * (0.3 + i * 0.3));
            ctx.fillText(text, 0, 0);
          }
        } else {
          ctx.fillText(text, 0, 0);
        }

        ctx.restore();
      }
      return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
    },
    fitWidth() {
      onChange({ ...mockup, height_pct: Math.min(60, (mockup.height_pct || 15) + 2) });
    },
    centerText() {
      onChange({ ...mockup, pos_x_pct: 50, pos_y_pct: 50 });
    },
  }));

  return (
    <div
      ref={containerRef}
      data-testid={WS.canvas}
      className={`relative w-full overflow-hidden rounded-md border ${night ? "border-zinc-900" : "border-zinc-800"} surface-card`}
      style={{ aspectRatio: "16/10", background: night ? "#050505" : "#1a1a1d", containerType: "size" }}
    >
      {storefrontPath ? (
        <img
          key={storefrontPath}
          ref={imgRef}
          src={`${API}/files/${storefrontPath}`}
          alt="storefront"
          onLoad={() => setImgLoaded(true)}
          crossOrigin="use-credentials"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: night ? "brightness(0.55) saturate(0.85)" : "none" }}
          draggable={false}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-zinc-600 text-sm">
          Upload a storefront photo to begin
        </div>
      )}
      {night && storefrontPath && <div className="absolute inset-0 bg-black/40 pointer-events-none" />}

      {/* Sign area bounding box for scale reference */}
      <div
        className="absolute pointer-events-none"
        style={{
          left: `${mockup.pos_x_pct - mockup.width_pct / 2}%`,
          top: `${mockup.pos_y_pct - mockup.height_pct / 2}%`,
          width: `${mockup.width_pct}%`,
          height: `${mockup.height_pct}%`,
          border: "1px dashed rgba(249,115,22,0.7)",
        }}
      />

      {/* Text overlay (draggable) — font-size set via container-query height units */}
      {imgLoaded && signText && (
        <div
          onMouseDown={startDrag}
          onTouchStart={startTouchDrag}
          className="absolute select-none cursor-move flex items-center justify-center"
          style={{
            left: `${mockup.pos_x_pct}%`,
            top: `${mockup.pos_y_pct}%`,
            width: `${mockup.width_pct}%`,
            height: `${mockup.height_pct}%`,
            transform: `translate(-50%, -50%) rotate(${mockup.rotation || 0}deg)`,
          }}
        >
          <div
            data-testid={WS.signText}
            className={`whitespace-nowrap font-bold leading-none ${mockup.shadow ? "sign-shadow" : ""} ${night && mockup.glow ? "sign-glow-night" : ""}`}
            style={{
              fontFamily: font.css,
              fontSize: `${mockup.height_pct * 0.92}cqh`,
              color: mockup.color,
              letterSpacing: `${mockup.letter_spacing || 0}px`,
            }}
          >
            {signText}
          </div>
          <div className="absolute -top-2 -left-2 bg-brand text-black text-[9px] px-1.5 py-0.5 rounded-sm font-bold tracking-widest uppercase pointer-events-none flex items-center gap-1">
            <Move className="w-2.5 h-2.5" /> Drag
          </div>
        </div>
      )}
    </div>
  );
});

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

export default MockupCanvas;
